
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShortContentItem } from '@/hooks/useShortContent';
import { format } from 'date-fns';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { Info, Lightbulb, Quote, Star, FileText, Eye } from 'lucide-react';

interface ShortContentCardProps {
  item: ShortContentItem;
  onView?: (id: string) => void;
  compact?: boolean;
  showActions?: boolean;
}

export function ShortContentCard({ item, onView, compact = false, showActions = true }: ShortContentCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  
  const getContentTypeIcon = () => {
    switch (item.content_type) {
      case 'tip': return <Lightbulb className="h-4 w-4" />;
      case 'fact': return <Info className="h-4 w-4" />;
      case 'quote': return <Quote className="h-4 w-4" />;
      case 'highlight': return <Star className="h-4 w-4" />;
      case 'snippet': return <FileText className="h-4 w-4" />;
      default: return null;
    }
  };
  
  const getContentTypeColor = () => {
    switch (item.content_type) {
      case 'tip': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'fact': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'quote': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'highlight': return 'bg-green-100 text-green-800 border-green-200';
      case 'snippet': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const handleClick = () => {
    if (onView) {
      onView(item.id);
    }
  };

  // Enhanced touch event handlers
  const handleTouchStart = () => {
    setIsPressed(true);
  };

  const handleTouchEnd = () => {
    setIsPressed(false);
  };

  const handleTouchCancel = () => {
    setIsPressed(false);
  };
  
  // Enhanced card className with touch optimizations
  const cardClassName = `
    h-full flex flex-col transition-all duration-300 cursor-pointer group
    ${isPressed ? 'scale-95 shadow-sm' : 'hover:shadow-lg hover:-translate-y-1'}
    ${isHovered ? 'shadow-lg -translate-y-1' : ''}
    touch-manipulation select-none
    active:scale-95 active:shadow-sm
    focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
  `.replace(/\s+/g, ' ').trim();
  
  return (
    <Card 
      className={cardClassName}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      tabIndex={0}
      role="button"
      aria-label={`View ${item.content_type}: ${item.title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {item.image_url && (
        <div className="relative w-full pt-[60%] overflow-hidden rounded-t-lg">
          <img 
            src={item.image_url} 
            alt={item.title} 
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          {item.is_featured && (
            <Badge className="absolute top-2 right-2 bg-yellow-500 text-yellow-900 border-yellow-400 shadow-sm">
              <Star className="h-3 w-3 mr-1" />
              Featured
            </Badge>
          )}
          {/* Enhanced view count overlay with better touch targets */}
          {item.view_count > 0 && (
            <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 min-h-[24px]">
              <Eye className="h-3 w-3" />
              {item.view_count}
            </div>
          )}
        </div>
      )}
      
      {/* Enhanced header with better touch spacing */}
      <CardHeader className="pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
        <div className="flex items-center justify-between gap-2">
          <Badge 
            variant="outline" 
            className={`flex items-center gap-1 transition-colors duration-200 ${getContentTypeColor()} min-h-[28px] px-2 py-1`}
          >
            {getContentTypeIcon()}
            <span className="capitalize text-xs sm:text-sm">{item.content_type}</span>
          </Badge>
          {item.category && (
            <Badge variant="secondary" className="text-xs opacity-75 group-hover:opacity-100 transition-opacity min-h-[24px]">
              {item.category}
            </Badge>
          )}
        </div>
        <h3 className="font-bold text-lg sm:text-xl mt-2 group-hover:text-primary transition-colors duration-200 line-clamp-2 leading-tight">
          {item.title}
        </h3>
      </CardHeader>
      
      {/* Enhanced content with better mobile spacing */}
      <CardContent className="pb-2 flex-grow px-4 sm:px-6">
        <div className="line-clamp-3 text-sm sm:text-base text-muted-foreground transition-colors duration-200 group-hover:text-foreground">
          <RichTextViewer content={item.content} />
        </div>
      </CardContent>
      
      {/* Enhanced footer with better mobile spacing */}
      <CardFooter className="pt-2 text-xs sm:text-sm text-muted-foreground border-t flex justify-between items-center px-4 sm:px-6 pb-4 sm:pb-6 min-h-[44px]">
        <span>
          {item.publish_date && format(new Date(item.publish_date), 'MMM d, yyyy')}
        </span>
        {item.source && (
          <span className="opacity-60 group-hover:opacity-100 transition-opacity truncate ml-2">
            {item.source}
          </span>
        )}
      </CardFooter>
      
      {/* Enhanced hover indicator */}
      <div className={`absolute inset-0 border-2 border-primary rounded-lg pointer-events-none transition-opacity duration-200 ${
        isHovered || isPressed ? 'opacity-20' : 'opacity-0'
      }`} />
    </Card>
  );
}
